import {OPTION_ACTION_REQUIRED_USER, OPTION_ASSIGNED_TO_USER, OPTION_CURRENT_USER, UsersSelectorGroupBy} from "../../@const"
import {PopoverMenuItemType} from "../../../../../gd-components/popover-menu/popover-menu/const"

export const GROUP_HEADER_TYPE = 'group-header'

// System user options must stay ungrouped and pinned above all groups (see _buildGrouped)
const SYSTEM_USER_OPTION_VALUES = new Set([
    OPTION_ACTION_REQUIRED_USER.value,
    OPTION_ASSIGNED_TO_USER.value,
    OPTION_CURRENT_USER.value,
])

export function buildDisplayOptions(
    field: any,
    searchString: string | null,
    groupBy: UsersSelectorGroupBy,
    collapsedGroups: Set<string>
): any[] {
    let options: any[] = field.getOptions().slice()

    if (searchString) {
        const lower = searchString.toLowerCase()
        options = options.filter((o: any) => o.label?.toLowerCase().includes(lower))
    }

    if (field.allValuesOption) {
        options.unshift({ isAllValuesOption: true, label: field.allValuesOptionLabel })
    }

    options = _buildGrouped(options, groupBy)
    options = _applyCollapsed(options, collapsedGroups)

    return options
}

// Returns all group titles for the current groupBy (ignores search/collapse).
// Used by collapseAll to know which groups exist.
export function getGroupTitles(field: any, groupBy: UsersSelectorGroupBy): string[] {
    return _buildGrouped(field.getOptions().slice(), groupBy)
        .filter((o: any) => o.type === GROUP_HEADER_TYPE)
        .map((o: any) => o.title)
}

function _buildGrouped(options: any[], groupBy: UsersSelectorGroupBy): any[] {
    const allNavigable = _.filter(options, (o: any) => {
        return !o.type
    })
    const systemOptions = _.filter(allNavigable, (o: any) => {
        return SYSTEM_USER_OPTION_VALUES.has(o.value)
    })
    const navigable = _.filter(allNavigable, (o: any) => {
        return !SYSTEM_USER_OPTION_VALUES.has(o.value)
    })

    if (!systemOptions.length) {
        return groupBy === UsersSelectorGroupBy.NONE
            ?
            options
            :
            _buildGroupedByType(options, navigable, groupBy)
    }

    // Separator after system options is shown even ungrouped (see FieldUsers_PopoverMenu)
    const rest = groupBy === UsersSelectorGroupBy.NONE
        ? _.filter(options, (o: any) => !SYSTEM_USER_OPTION_VALUES.has(o.value))
        : _buildGroupedByType(options, navigable, groupBy)

    return [
        ...systemOptions,
        {
            type: PopoverMenuItemType.SEPARATOR
        },
        ...rest
    ]
}

function _buildGroupedByType(options: any[], navigable: any[], groupBy: UsersSelectorGroupBy): any[] {
    if (groupBy === UsersSelectorGroupBy.DEPARTMENT) {
        const grouped = _.groupBy(navigable, (o: any) => {
            const cu = gd.session.companyUsers.getByUserId(o.value)
            return cu?.departmentId ?? '__none__'
        })

        const result: any[] = []
        const sorted = _(grouped)
            .toPairs()
            .sortBy(([deptId]: [string, any[]]) => {
                if (deptId === '__none__') return '￿'
                return gd.session.departments.get(deptId)?.name ?? ''
            })
            .value()

        for (const [deptId, users] of sorted as [string, any[]][]) {
            const name = deptId === '__none__'
                ? 'No Department'
                : (gd.session.departments.get(deptId)?.name ?? 'Unknown')
            result.push({ type: GROUP_HEADER_TYPE, title: name, groupValues: _.map(users, (u: any) => u.value) })
            result.push(...users)
        }
        return result
    }

    if (groupBy === UsersSelectorGroupBy.TEAMS) {
        const allTeams = _.sortBy(gd.session.teams.models, (t: any) => t.name)
        const optionValues = new Set(navigable.map((o: any) => o.value))
        const optionMap = _.keyBy(navigable, 'value')
        const usedInTeam = new Set<string>()
        const shownInResult = new Set<string>()
        const result: any[] = []

        for (const team of allTeams as any[]) {
            const members = (team.userIds as string[]).filter(uid => optionValues.has(uid))
            if (!members.length) continue
            members.forEach(uid => usedInTeam.add(uid))
            // groupValues has all members (for select-all checkbox); render each user once
            const toRender = members.filter(uid => !shownInResult.has(uid))
            toRender.forEach(uid => shownInResult.add(uid))
            result.push({ type: GROUP_HEADER_TYPE, title: team.name, groupValues: members })
            result.push(...toRender.map(uid => optionMap[uid]))
        }

        const noTeam = navigable.filter((o: any) => !usedInTeam.has(o.value))
        if (noTeam.length) {
            result.push({ type: GROUP_HEADER_TYPE, title: 'No Team', groupValues: _.map(noTeam, (u: any) => u.value) })
            result.push(...noTeam)
        }
        return result
    }

    return options
}

// Stamps isCollapsed onto group headers and removes their members when collapsed.
function _applyCollapsed(options: any[], collapsedGroups: Set<string>): any[] {
    const result: any[] = []
    let currentGroupCollapsed = false

    for (const option of options) {
        if (option.type === GROUP_HEADER_TYPE) {
            currentGroupCollapsed = collapsedGroups.size > 0 && collapsedGroups.has(option.title)
            result.push({ ...option, isCollapsed: currentGroupCollapsed })
        } else if (currentGroupCollapsed) {
            // skip members of collapsed group
        } else {
            result.push(option)
        }
    }
    return result
}
